'use client';

import { ScoredTechnology } from '@/lib/tech-stack-recommender';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  Cell,
} from 'recharts';

interface ChartsProps {
  technologies: ScoredTechnology[];
}

const COLORS = ['#E89841', '#4A90E2', '#50C878', '#9B59B6', '#E74C3C'];

export default function TechStackCharts({ technologies }: ChartsProps) {
  const top5 = technologies.slice(0, 5);

  return (
    <div className="space-y-8">
      {/* Radar Chart - Multi-dimensional comparison */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">Multi-Dimensional Comparison</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Compare all scoring dimensions across technologies
        </p>
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={getRadarData(top5)}>
            <PolarGrid stroke="#E8D5B7" strokeOpacity={0.2} />
            <PolarAngleAxis dataKey="dimension" tick={{ fill: '#E8D5B7', fontSize: 12 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#E8D5B7', fontSize: 10 }} />
            {top5.map((tech, index) => (
              <Radar
                key={tech.id}
                name={tech.name}
                dataKey={tech.id}
                stroke={COLORS[index]}
                fill={COLORS[index]}
                fillOpacity={0.2}
              />
            ))}
            <Legend wrapperStyle={{ color: '#E8D5B7' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0A1E2E',
                border: '1px solid #E8D5B7',
                borderRadius: '8px',
                color: '#E8D5B7',
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart - Score breakdown */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">Score Breakdown by Dimension</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Detailed scoring across all evaluation criteria
        </p>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={getBarData(top5)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8D5B7" strokeOpacity={0.1} />
            <XAxis dataKey="name" tick={{ fill: '#E8D5B7', fontSize: 12 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#E8D5B7', fontSize: 10 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0A1E2E',
                border: '1px solid #E8D5B7',
                borderRadius: '8px',
                color: '#E8D5B7',
              }}
            />
            <Legend wrapperStyle={{ color: '#E8D5B7' }} />
            <Bar dataKey="compliance" stackId="a" fill="#E89841" name="Compliance" />
            <Bar dataKey="technical" stackId="a" fill="#4A90E2" name="Technical" />
            <Bar dataKey="operational" stackId="a" fill="#50C878" name="Operational" />
            <Bar dataKey="cost" stackId="a" fill="#9B59B6" name="Cost" />
            <Bar dataKey="ecosystem" stackId="a" fill="#E74C3C" name="Ecosystem" />
            <Bar dataKey="strategic" stackId="a" fill="#F39C12" name="Strategic" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Scatter Plot - Cost vs Performance */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">Cost vs Performance Analysis</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Find the best value proposition (top-right = best value)
        </p>
        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid stroke="#E8D5B7" strokeOpacity={0.1} />
            <XAxis
              type="number"
              dataKey="cost"
              name="Cost Score"
              domain={[0, 100]}
              tick={{ fill: '#E8D5B7', fontSize: 10 }}
              label={{ value: 'Cost Score (higher = better value)', position: 'bottom', fill: '#E8D5B7', fontSize: 12 }}
            />
            <YAxis
              type="number"
              dataKey="performance"
              name="Performance Score"
              domain={[0, 100]}
              tick={{ fill: '#E8D5B7', fontSize: 10 }}
              label={{ value: 'Performance Score', angle: -90, position: 'left', fill: '#E8D5B7', fontSize: 12 }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={{
                backgroundColor: '#0A1E2E',
                border: '1px solid #E8D5B7',
                borderRadius: '8px',
                color: '#E8D5B7',
              }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-background border border-border rounded-lg p-3">
                      <p className="font-semibold text-foreground">{data.name}</p>
                      <p className="text-sm text-foreground-secondary">Cost Score: {data.cost}</p>
                      <p className="text-sm text-foreground-secondary">Performance: {data.performance}</p>
                      <p className="text-sm text-accent">Overall: {data.overall}/100</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter name="Technologies" data={getScatterData(top5)} fill="#E89841">
              {getScatterData(top5).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index]} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Overall Score Comparison */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">Overall Score Comparison</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Total weighted score for each technology
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getOverallScoreData(top5)} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E8D5B7" strokeOpacity={0.1} />
            <XAxis type="number" domain={[0, 100]} tick={{ fill: '#E8D5B7', fontSize: 10 }} />
            <YAxis dataKey="name" type="category" tick={{ fill: '#E8D5B7', fontSize: 12 }} width={150} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0A1E2E',
                border: '1px solid #E8D5B7',
                borderRadius: '8px',
                color: '#E8D5B7',
              }}
            />
            <Bar dataKey="score" fill="#E89841" radius={[0, 8, 8, 0]}>
              {getOverallScoreData(top5).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Match Reasons */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">Why These Technologies?</h3>
        <div className="space-y-6">
          {top5.map((tech, index) => (
            <div key={tech.id} className="border-l-4 pl-4" style={{ borderColor: COLORS[index] }}>
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-foreground">{tech.name}</h4>
                <span className="text-sm font-semibold" style={{ color: COLORS[index] }}>
                  {tech.score.toFixed(1)}/100
                </span>
              </div>

              {tech.matchReasons.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-foreground-secondary mb-1">✓ Match Reasons:</p>
                  <ul className="space-y-1">
                    {tech.matchReasons.map((reason, i) => (
                      <li key={i} className="text-sm text-foreground/90 flex items-start">
                        <span className="text-accent mr-2">•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tech.concerns.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-foreground-secondary mb-1">⚠ Considerations:</p>
                  <ul className="space-y-1">
                    {tech.concerns.map((concern, i) => (
                      <li key={i} className="text-sm text-foreground-secondary flex items-start">
                        <span className="text-foreground-muted mr-2">•</span>
                        <span>{concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getRadarData(technologies: ScoredTechnology[]) {
  const dimensions = ['compliance', 'technical', 'operational', 'cost', 'ecosystem', 'strategic'];
  const labels: Record<string, string> = {
    compliance: 'Compliance',
    technical: 'Technical',
    operational: 'Operations',
    cost: 'Cost',
    ecosystem: 'Ecosystem',
    strategic: 'Strategic',
  };

  return dimensions.map(dim => {
    const point: any = { dimension: labels[dim] };
    technologies.forEach(tech => {
      point[tech.id] = Math.round(tech.scoreBreakdown[dim as keyof typeof tech.scoreBreakdown] * 100);
    });
    return point;
  });
}

function getBarData(technologies: ScoredTechnology[]) {
  return technologies.map(tech => ({
    name: tech.name,
    compliance: Math.round(tech.scoreBreakdown.compliance * 100),
    technical: Math.round(tech.scoreBreakdown.technical * 100),
    operational: Math.round(tech.scoreBreakdown.operational * 100),
    cost: Math.round(tech.scoreBreakdown.cost * 100),
    ecosystem: Math.round(tech.scoreBreakdown.ecosystem * 100),
    strategic: Math.round(tech.scoreBreakdown.strategic * 100),
  }));
}

function getScatterData(technologies: ScoredTechnology[]) {
  return technologies.map(tech => ({
    name: tech.name,
    cost: Math.round(tech.scoreBreakdown.cost * 100),
    performance: Math.round(tech.scoreBreakdown.technical * 100),
    overall: Math.round(tech.score),
  }));
}

function getOverallScoreData(technologies: ScoredTechnology[]) {
  return technologies.map(tech => ({
    name: tech.name,
    score: Math.round(tech.score),
  }));
}
